// utils/bugLog.js
// Lightweight, dependency-free capture buffer backing the "Report a bug" button
// (components/BugReporter.jsx). Ported from swa-rprs/client/src/utils/bugLog.js.
//
// Two ring buffers in module scope: recent console.* lines + window error events.
// installBugLog() patches console.* once (idempotent) and registers the window
// listeners; call it at app boot (index.js) BEFORE React renders. Originals are
// always called through, so dev-tools behavior is unchanged.

const LOG_LIMIT = 120;
const EVENT_LIMIT = 40;
const MAX_ARG_LEN = 2000;

const logBuffer = [];
const eventBuffer = [];
let installed = false;

function push(buf, entry, limit) {
  buf.push(entry);
  if (buf.length > limit) buf.splice(0, buf.length - limit);
}

// Best-effort, never-throw serialization of a single console argument.
function serializeArg(arg) {
  if (arg == null) return String(arg);
  if (typeof arg === 'string') return arg.length > MAX_ARG_LEN ? arg.slice(0, MAX_ARG_LEN) + '…' : arg;
  if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
  if (arg instanceof Error) return `${arg.name}: ${arg.message}${arg.stack ? '\n' + arg.stack : ''}`.slice(0, MAX_ARG_LEN);
  try {
    const seen = new WeakSet();
    const s = JSON.stringify(arg, (_k, v) => {
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[Circular]';
        seen.add(v);
      }
      return v;
    });
    return s == null ? String(arg) : (s.length > MAX_ARG_LEN ? s.slice(0, MAX_ARG_LEN) + '…' : s);
  } catch {
    try { return String(arg).slice(0, MAX_ARG_LEN); } catch { return '[unserializable]'; }
  }
}

export function installBugLog() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const c = console;
  const levels = ['log', 'info', 'warn', 'error', 'debug'];
  for (const level of levels) {
    const original = c[level] ? c[level].bind(console) : null;
    c[level] = (...args) => {
      try {
        push(logBuffer, { level, ts: new Date().toISOString(), msg: args.map(serializeArg).join(' ') }, LOG_LIMIT);
      } catch { /* never let logging break the app */ }
      if (original) original(...args);
    };
  }

  window.addEventListener('error', (e) => {
    push(eventBuffer, {
      type: 'window.error', ts: new Date().toISOString(),
      message: (e && e.message) || String(e), source: (e && e.filename) || null,
      line: e && e.lineno != null ? e.lineno : null, col: e && e.colno != null ? e.colno : null,
      stack: e && e.error && e.error.stack ? String(e.error.stack).slice(0, MAX_ARG_LEN) : null,
    }, EVENT_LIMIT);
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e && e.reason;
    push(eventBuffer, {
      type: 'unhandledrejection', ts: new Date().toISOString(),
      message: reason instanceof Error ? `${reason.name}: ${reason.message}` : serializeArg(reason),
      stack: reason instanceof Error && reason.stack ? String(reason.stack).slice(0, MAX_ARG_LEN) : null,
    }, EVENT_LIMIT);
  });
}

// Snapshots — copies so callers can't mutate the live buffers.
export function snapshotLogs() { return logBuffer.slice(); }
export function snapshotEvents() { return eventBuffer.slice(); }

// Stable client-side context anyone can attach to a report.
export function snapshotClientContext() {
  if (typeof window === 'undefined') return {};
  return {
    url: window.location.href,
    route: window.location.pathname + window.location.search + window.location.hash,
    referrer: document.referrer || null,
    viewport: { width: window.innerWidth, height: window.innerHeight, devicePixelRatio: window.devicePixelRatio || 1 },
    screen: { width: (window.screen && window.screen.width) || null, height: (window.screen && window.screen.height) || null },
    userAgent: navigator.userAgent,
    language: navigator.language,
    online: navigator.onLine,
    capturedAt: new Date().toISOString(),
  };
}
